/**
 * Schema Markup Utilities
 *
 * Generates JSON-LD structured data for improved SEO and rich snippets in search results.
 * Implements schema.org types: Organization, VideoGame, Article, BreadcrumbList, etc.
 */

import { SITE_METADATA } from '~/lib/site-metadata';

export interface SchemaOrgBase {
  '@context': 'https://schema.org';
  '@type': string;
}

export interface Organization extends SchemaOrgBase {
  '@type': 'Organization';
  name: string;
  url: string;
  logo: string;
  description: string;
  sameAs: string[];
  contactPoint?: {
    '@type': 'ContactPoint';
    contactType: string;
    email: string;
  };
}

export interface VideoGame extends SchemaOrgBase {
  '@type': 'VideoGame' | 'Product';
  name: string;
  description: string;
  image: string;
  genre: string[];
  gamePlatform: string[];
  publisher: Organization;
  offers?: {
    '@type': 'Offer';
    availability: string;
    price?: string;
    priceCurrency?: string;
  };
  aggregateRating?: {
    '@type': 'AggregateRating';
    ratingValue: number;
    ratingCount: number;
    bestRating: number;
  };
}

export interface Article extends SchemaOrgBase {
  '@type': 'Article' | 'BlogPosting' | 'TechArticle';
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified?: string;
  author: {
    '@type': 'Person' | 'Organization';
    name: string;
    url?: string;
  };
  publisher: Organization;
  mainEntityOfPage: {
    '@type': 'WebPage';
    '@id': string;
  };
}

export interface BreadcrumbList extends SchemaOrgBase {
  '@type': 'BreadcrumbList';
  itemListElement: Array<{
    '@type': 'ListItem';
    position: number;
    name: string;
    item?: string;
  }>;
}

export interface WebSite extends SchemaOrgBase {
  '@type': 'WebSite';
  name: string;
  url: string;
  description: string;
  publisher: Organization;
  potentialAction?: {
    '@type': 'SearchAction';
    target: {
      '@type': 'EntryPoint';
      urlTemplate: string;
    };
    'query-input': string;
  };
}

/**
 * Generate Organization schema for the company/team
 */
export function createOrganizationSchema(): Organization {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_METADATA.title,
    url: SITE_METADATA.url,
    logo: `${SITE_METADATA.url}/logo.png`,
    description: SITE_METADATA.description,
    sameAs: [
      'https://twitter.com/lumoragame',
      'https://discord.gg/lumora',
      'https://reddit.com/r/lumora',
      'https://github.com/lumora',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Support',
      email: 'support@lumora.com',
    },
  };
}

/**
 * Generate VideoGame schema for the game
 */
export function createVideoGameSchema(): VideoGame {
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoGame',
    name: 'Lumora: The First Dawn',
    description: SITE_METADATA.description,
    image: `${SITE_METADATA.url}/og-image.jpg`,
    genre: ['Role-Playing Game', 'Fantasy', 'Adventure', 'RPG'],
    gamePlatform: ['PC', 'Windows', 'macOS', 'Steam'],
    publisher: createOrganizationSchema(),
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/PreOrder',
    },
  };
}

/**
 * Generate Article schema for blog posts
 */
export function createArticleSchema({
  title,
  description,
  image,
  url,
  datePublished,
  dateModified,
  authorName = 'Lumora Team',
  authorUrl,
  type = 'BlogPosting',
}: {
  title: string;
  description: string;
  image: string;
  url: string;
  datePublished: string;
  dateModified?: string;
  authorName?: string;
  authorUrl?: string;
  type?: 'Article' | 'BlogPosting' | 'TechArticle';
}): Article {
  return {
    '@context': 'https://schema.org',
    '@type': type,
    headline: title,
    description,
    image,
    datePublished,
    dateModified: dateModified || datePublished,
    author: {
      '@type': authorUrl ? 'Person' : 'Organization',
      name: authorName,
      ...(authorUrl && { url: authorUrl }),
    },
    publisher: createOrganizationSchema(),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
  };
}

/**
 * Generate BreadcrumbList schema for navigation
 */
export function createBreadcrumbSchema(
  breadcrumbs: Array<{ name: string; url?: string }>
): BreadcrumbList {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      ...(crumb.url && { item: crumb.url }),
    })),
  };
}

/**
 * Generate WebSite schema with search functionality
 */
export function createWebSiteSchema(): WebSite {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_METADATA.title,
    url: SITE_METADATA.url,
    description: SITE_METADATA.description,
    publisher: createOrganizationSchema(),
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_METADATA.url}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

/**
 * Component to render JSON-LD schema in <head>
 */
export function SchemaMarkup({ schema }: { schema: SchemaOrgBase | SchemaOrgBase[] }) {
  const schemaString = JSON.stringify(Array.isArray(schema) ? schema : [schema], null, 2);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: schemaString }}
    />
  );
}
